import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectOrderHasExternalDepreciation } from '../selectOrderDepreciation.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { priceJSONToPrice } from '../utils/priceJSONToPrice.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateExternalObsolescenceAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  orderHasExternalDepreciation: boolean,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];
  if (
    !orderHasExternalDepreciation ||
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements)
  ) {
    return undefined;
  }

  const { externalDepreciation } = subjectImprovements;
  const { externalDepreciation: compExternalDepreciation } = compImprovements;

  const externalDepreciationParsed = priceJSONToPrice(externalDepreciation) ?? AloftPrice.zero;
  const compExternalDepreciationParsed =
    priceJSONToPrice(compExternalDepreciation) ?? AloftPrice.zero;

  // Comp - Subject, since the depreciation values are stored as positive values
  return compExternalDepreciationParsed.sub(externalDepreciationParsed);
};

const selectExternalObsolescenceAdj = createPriceSelector(
  [selectImprovementsEntities, selectOrderHasExternalDepreciation, (_state, id: string) => id],
  calculateExternalObsolescenceAdj,
);

export const selectExternalObsolescenceAdjs = createSelector(
  [selectImprovementsEntities, selectOrderHasExternalDepreciation, selectCompSequence],
  (improvements, orderHasExternalDepreciation, compSequence) => {
    return compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateExternalObsolescenceAdj(
        improvements,
        orderHasExternalDepreciation,
        propID,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {});
  },
);

const selectAppliedExternalObsolescenceAdj = createSelector(
  [selectExternalObsolescenceAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'externalObsolescence', adjSettings);
  },
);

export const selectAppliedExternalObsolescenceAdjDisplay = createSelector(
  [selectAppliedExternalObsolescenceAdj],
  (adj) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedExternalObsolescenceAdjsDisplay = createSelector(
  [selectCompSequence, selectExternalObsolescenceAdjs, selectAdjustmentSettings],
  (compSequence, conditionAdjs, adjSettings) => {
    return compSequence.reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = conditionAdjs[propID];

      if (isDefined(adj)) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'externalObsolescence', adjSettings),
          ),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {});
  },
);
